'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function BodySurfaceAreaCalculator() {
  const t = useTranslations("tools.bodySurfaceAreaCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    gender: 'male',
    weight: '',
    weightUnit: 'lbs',
    heightFeet: '',
    heightInches: '',
    heightCm: ''
  });

  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const baseStyles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative' as const,
      zIndex: 1,
    },
    header: {
      textAlign: 'center' as const,
    },
    h1: {
      fontWeight: 700,
      color: colors.textPrimary,
      lineHeight: 1.2,
    },
    summaryText: {
      color: colors.textSecondary,
      lineHeight: 1.6,
    },
    calculatorTool: {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
    },
    toolContainer: {
      maxWidth: '100%',
      margin: '0 auto',
    },
    toolHeader: {
      textAlign: 'center' as const,
    },
    h2: {
      fontWeight: 600,
      color: colors.textPrimary,
    },
    toolSubtitle: {
      color: colors.textSecondary,
    },
    calculatorForm: {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    formGroup: {},
    label: {
      display: 'block',
      color: '#334155',
      fontWeight: 600,
      fontSize: '0.95rem',
    },
    radioGroup: {
      display: 'flex',
      gap: '1.5rem',
    },
    radioOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer',
    },
    radio: {
      width: '1.25rem',
      height: '1.25rem',
      cursor: 'pointer',
      accentColor: '#2563eb',
    },
    radioLabel: {
      margin: 0,
      cursor: 'pointer',
      fontWeight: 500,
    },
    formRow: {
      display: 'grid',
      gap: '1.5rem',
    },
    inputWrapper: {
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'center',
    },
    input: {
      flex: 1,
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      width: '100%',
    },
    select: {
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      background: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      minWidth: '140px',
    },
    heightInputs: {
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'center',
    },
    heightLabel: {
      color: '#64748b',
      fontSize: '0.9rem',
      fontWeight: 500,
    },
    orDivider: {
      textAlign: 'center' as const,
      margin: '1rem 0',
      color: '#94a3b8',
      fontWeight: 600,
      fontSize: '0.9rem',
    },
    buttonGroup: {
      display: 'flex',
      gap: '1rem',
    },
    calculateButton: {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
    },
    clearButton: {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
    },
    errorMessage: {
      background: '#fee2e2',
      color: '#991b1b',
      padding: '1rem',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #dc2626',
    },
    results: {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      animation: 'slideIn 0.4s ease',
    },
    resultsTitle: {
      color: colors.textPrimary,
      textAlign: 'center' as const,
      fontWeight: 700,
    },
    resultsGrid: {
      display: 'grid',
      gap: '1rem',
    },
    resultCard: {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '0.75rem',
      borderLeft: '4px solid #2563eb',
    },
    resultCardPrimary: {
      background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
      borderLeft: '4px solid #2563eb',
    },
    resultLabel: {
      display: 'block',
      color: '#64748b',
      fontSize: '0.9rem',
      fontWeight: 600,
    },
    resultValue: {
      display: 'block',
      color: colors.textPrimary,
      fontWeight: 700,
    },
    resultValuePrimary: {
      color: '#2563eb',
    },
    formulasSection: {
      paddingTop: '2rem',
      borderTop: '2px solid #e2e8f0',
    },
    formulaGrid: {
      display: 'grid',
      gap: '1rem',
    },
    formulaItem: {
      background: '#f8fafc',
      padding: '1rem',
      borderRadius: '0.5rem',
      border: '1px solid #e2e8f0',
    },
    formulaName: {
      fontWeight: 600,
      color: '#475569',
      marginBottom: '0.25rem',
      fontSize: '0.9rem',
    },
    formulaValue: {
      color: '#2563eb',
      fontSize: '1.25rem',
      fontWeight: 700,
    },
    infoBox: {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderRadius: '0.75rem',
      borderLeft: '4px solid #f59e0b',
    },
    infoBoxText: {
      color: '#78350f',
      margin: 0,
      fontSize: '0.95rem',
      lineHeight: 1.6,
    },
    glassCard: {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
    },
    paragraph: {
      lineHeight: 1.6,
      color: colors.textSecondary,
    },
    list: {
      lineHeight: 1.8,
      color: colors.textSecondary,
    },
    listItem: {
      marginBottom: '0.75rem',
    },
    strong: {
      color: colors.textPrimary,
      fontWeight: 600,
    },
    insightCard: {
      background: 'linear-gradient(135deg, rgba(255, 247, 237, 0.9) 0%, rgba(254, 243, 199, 0.9) 100%)',
      borderLeft: `4px solid ${colors.secondary}`,
      borderRadius: '0.75rem',
    },
    faqItem: {
      background: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '0.75rem',
      border: '1px solid rgba(255, 200, 180, 0.15)',
    },
    faqQuestion: {
      fontWeight: 600,
      color: colors.textPrimary,
    },
    faqAnswer: {
      color: colors.textSecondary,
      lineHeight: 1.6,
    },
    referenceList: {
      listStyleType: 'none',
      padding: 0,
    },
    referenceItem: {
      background: 'rgba(255, 255, 255, 0.5)',
      padding: '1rem',
      borderRadius: '0.5rem',
      border: '1px solid rgba(255, 200, 180, 0.15)',
    },
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      fontWeight: 500,
      wordBreak: 'break-word' as const,
    },
  };

  const getHeightInCm = () => {
    if (inputs.heightCm) {
      return parseFloat(inputs.heightCm);
    } else if (inputs.heightFeet || inputs.heightInches) {
      const feet = parseFloat(inputs.heightFeet) || 0;
      const inches = parseFloat(inputs.heightInches) || 0;
      const totalInches = (feet * 12) + inches;
      return totalInches * 2.54;
    }
    return null;
  };

  const getWeightInKg = () => {
    const weight = parseFloat(inputs.weight);
    if (inputs.weightUnit === 'lbs') {
      return weight * 0.453592;
    }
    return weight;
  };

  const calculateBSA = (weightKg: number, heightCm: number, gender: string) => {
    const W = weightKg;
    const H = heightCm;

    const mosteller = 0.016667 * Math.pow(W, 0.5) * Math.pow(H, 0.5);
    const dubois = 0.007184 * Math.pow(W, 0.425) * Math.pow(H, 0.725);
    const haycock = 0.024265 * Math.pow(W, 0.5378) * Math.pow(H, 0.3964);
    const gehan = 0.0235 * Math.pow(W, 0.51456) * Math.pow(H, 0.42246);
    const boyd = 0.03330 * Math.pow(W, (0.6157 - 0.0188 * Math.log10(W))) * Math.pow(H, 0.3);
    const fujimoto = 0.008883 * Math.pow(W, 0.444) * Math.pow(H, 0.663);
    const takahira = 0.007241 * Math.pow(W, 0.425) * Math.pow(H, 0.725);

    let schlich;
    if (gender === 'female') {
      schlich = 0.000975482 * Math.pow(W, 0.46) * Math.pow(H, 1.08);
    } else {
      schlich = 0.000579479 * Math.pow(W, 0.38) * Math.pow(H, 1.24);
    }

    return {
      mosteller,
      dubois,
      haycock,
      gehan,
      boyd,
      fujimoto,
      takahira,
      schlich
    };
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const heightCmValue = getHeightInCm();
    const weightKg = getWeightInKg();

    if (!inputs.weight) {
      setError(t('errorWeight'));
      setResults(null);
      return;
    }

    if (!heightCmValue) {
      setError(t('errorHeight'));
      setResults(null);
      return;
    }

    if (weightKg <= 0 || heightCmValue <= 0) {
      setError(t('errorPositive'));
      setResults(null);
      return;
    }

    const calculatedResults = calculateBSA(weightKg, heightCmValue, inputs.gender);
    setResults(calculatedResults);
  };

  const handleClear = () => {
    setInputs({
      gender: 'male',
      weight: '',
      weightUnit: 'lbs',
      heightFeet: '',
      heightInches: '',
      heightCm: ''
    });
    setResults(null);
    setError('');
  };

  const handleHeightFeetChange = (value: string) => {
    setInputs({ ...inputs, heightFeet: value, heightCm: '' });
  };

  const handleHeightInchesChange = (value: string) => {
    setInputs({ ...inputs, heightInches: value, heightCm: '' });
  };

  const handleHeightCmChange = (value: string) => {
    setInputs({ ...inputs, heightCm: value, heightFeet: '', heightInches: '' });
  };

  return (
    <div style={getResponsiveStyle(baseStyles.container,
      { padding: '1rem' },
      { padding: '2rem' },
      { padding: '3rem' }
    )}>
      {/* Header */}
      <header style={getResponsiveStyle(baseStyles.header,
        { marginBottom: '2rem' },
        { marginBottom: '2.5rem' },
        { marginBottom: '3rem' }
      )}>
        <h1 style={getResponsiveStyle(baseStyles.h1,
          { fontSize: '2rem', marginBottom: '0.75rem' },
          { fontSize: '2.5rem', marginBottom: '1rem' },
          { fontSize: '3rem', marginBottom: '1.25rem' }
        )}>{t('title')}</h1>
        <p style={getResponsiveStyle(baseStyles.summaryText,
          { fontSize: '1rem' },
          { fontSize: '1.125rem' },
          { fontSize: '1.25rem' }
        )}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={getResponsiveStyle(baseStyles.calculatorTool,
        { padding: '1.5rem 1rem', marginBottom: '2rem' },
        { padding: '2rem 1.5rem', marginBottom: '2.5rem' },
        { padding: '3rem 2rem', marginBottom: '3rem' }
      )}>
        <div style={getResponsiveStyle(baseStyles.toolContainer,
          {},
          { maxWidth: '700px' },
          { maxWidth: '900px' }
        )}>
          <div style={getResponsiveStyle(baseStyles.toolHeader,
            { marginBottom: '1.5rem' },
            { marginBottom: '2rem' },
            { marginBottom: '2.5rem' }
          )}>
            <h2 style={getResponsiveStyle(baseStyles.h2,
              { fontSize: '1.5rem', marginBottom: '0.5rem' },
              { fontSize: '1.75rem', marginBottom: '0.5rem' },
              { fontSize: '2rem', marginBottom: '0.5rem' }
            )}>{t('toolTitle')}</h2>
            <p style={getResponsiveStyle(baseStyles.toolSubtitle,
              { fontSize: '0.9rem' },
              { fontSize: '1rem' },
              { fontSize: '1rem' }
            )}>{t('toolSubtitle')}</p>
          </div>

          <form onSubmit={handleCalculate} style={getResponsiveStyle(baseStyles.calculatorForm,
            { padding: '1.5rem', marginBottom: '1.5rem' },
            { padding: '2rem', marginBottom: '2rem' },
            { padding: '2rem', marginBottom: '2rem' }
          )}>
            <div style={getResponsiveStyle(baseStyles.formGroup,
              { marginBottom: '1.5rem' },
              { marginBottom: '1.5rem' },
              { marginBottom: '1.5rem' }
            )}>
              <label style={getResponsiveStyle(baseStyles.label,
                { marginBottom: '0.5rem' },
                { marginBottom: '0.5rem' },
                { marginBottom: '0.5rem' }
              )}>{t('genderLabel')}</label>
              <div style={baseStyles.radioGroup}>
                <div style={baseStyles.radioOption}>
                  <input
                    type="radio"
                    id="gender-male"
                    name="gender"
                    value="male"
                    checked={inputs.gender === 'male'}
                    onChange={(e) => setInputs({ ...inputs, gender: e.target.value })}
                    style={baseStyles.radio}
                  />
                  <label htmlFor="gender-male" style={baseStyles.radioLabel}>{t('genderMale')}</label>
                </div>
                <div style={baseStyles.radioOption}>
                  <input
                    type="radio"
                    id="gender-female"
                    name="gender"
                    value="female"
                    checked={inputs.gender === 'female'}
                    onChange={(e) => setInputs({ ...inputs, gender: e.target.value })}
                    style={baseStyles.radio}
                  />
                  <label htmlFor="gender-female" style={baseStyles.radioLabel}>{t('genderFemale')}</label>
                </div>
              </div>
            </div>

            <div style={getResponsiveStyle(baseStyles.formRow,
              { gridTemplateColumns: '1fr', marginBottom: '1.5rem' },
              { gridTemplateColumns: '1fr', marginBottom: '1.5rem' },
              { gridTemplateColumns: '1fr', marginBottom: '1.5rem' }
            )}>
              <div style={baseStyles.formGroup}>
                <label htmlFor="weight" style={getResponsiveStyle(baseStyles.label,
                  { marginBottom: '0.5rem' },
                  { marginBottom: '0.5rem' },
                  { marginBottom: '0.5rem' }
                )}>{t('weightLabel')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    id="weight"
                    step="0.1"
                    min="0"
                    value={inputs.weight}
                    onChange={(e) => setInputs({ ...inputs, weight: e.target.value })}
                    style={getResponsiveStyle(baseStyles.input,
                      { padding: '0.75rem 1rem' },
                      { padding: '0.875rem 1.25rem' },
                      { padding: '0.75rem 1rem' }
                    )}
                  />
                  <select
                    id="weight-unit"
                    value={inputs.weightUnit}
                    onChange={(e) => setInputs({ ...inputs, weightUnit: e.target.value })}
                    style={baseStyles.select}
                  >
                    <option value="kg">{t('weightKg')}</option>
                    <option value="lbs">{t('weightLbs')}</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={getResponsiveStyle(baseStyles.formGroup,
              { marginBottom: '1.5rem' },
              { marginBottom: '1.5rem' },
              { marginBottom: '1.5rem' }
            )}>
              <label style={getResponsiveStyle(baseStyles.label,
                { marginBottom: '0.5rem' },
                { marginBottom: '0.5rem' },
                { marginBottom: '0.5rem' }
              )}>{t('heightLabel')}</label>
              <div style={baseStyles.heightInputs}>
                <input
                  type="number"
                  id="height-feet"
                  placeholder={t('heightFeetPlaceholder')}
                  min="0"
                  step="1"
                  value={inputs.heightFeet}
                  onChange={(e) => handleHeightFeetChange(e.target.value)}
                  style={getResponsiveStyle(baseStyles.input,
                    { padding: '0.75rem 1rem' },
                    { padding: '0.875rem 1.25rem' },
                    { padding: '0.75rem 1rem' }
                  )}
                />
                <span style={baseStyles.heightLabel}>{t('heightFt')}</span>
                <input
                  type="number"
                  id="height-inches"
                  placeholder={t('heightInchesPlaceholder')}
                  min="0"
                  max="11"
                  step="0.1"
                  value={inputs.heightInches}
                  onChange={(e) => handleHeightInchesChange(e.target.value)}
                  style={getResponsiveStyle(baseStyles.input,
                    { padding: '0.75rem 1rem' },
                    { padding: '0.875rem 1.25rem' },
                    { padding: '0.75rem 1rem' }
                  )}
                />
                <span style={baseStyles.heightLabel}>{t('heightIn')}</span>
              </div>

              <div style={baseStyles.orDivider}>{t('orDivider')}</div>

              <div style={baseStyles.inputWrapper}>
                <input
                  type="number"
                  id="height-cm"
                  placeholder={t('heightCmPlaceholder')}
                  min="0"
                  step="0.1"
                  value={inputs.heightCm}
                  onChange={(e) => handleHeightCmChange(e.target.value)}
                  style={getResponsiveStyle(baseStyles.input,
                    { padding: '0.75rem 1rem' },
                    { padding: '0.875rem 1.25rem' },
                    { padding: '0.75rem 1rem' }
                  )}
                />
                <span style={{ minWidth: '40px', textAlign: 'center', color: '#64748b', fontSize: '0.9rem', fontWeight: 500 }}>{t('heightCm')}</span>
              </div>
            </div>

            {error && (
              <div style={getResponsiveStyle(baseStyles.errorMessage,
                { marginTop: '1rem', fontSize: '0.9rem' },
                { marginTop: '1rem', fontSize: '0.95rem' },
                { marginTop: '1rem', fontSize: '0.95rem' }
              )}>
                {error}
              </div>
            )}

            <div style={getResponsiveStyle(baseStyles.buttonGroup,
              { marginTop: '2rem', flexDirection: 'column' as const },
              { marginTop: '2rem', flexDirection: 'row' as const },
              { marginTop: '2rem', flexDirection: 'row' as const }
            )}>
              <button type="submit" style={getResponsiveStyle(baseStyles.calculateButton,
                { padding: '0.875rem 1.5rem', fontSize: '1rem' },
                { padding: '1rem 2rem', fontSize: '1.05rem' },
                { padding: '1rem 2rem', fontSize: '1.05rem' }
              )}>
                {t('calculateButton')}
              </button>
              <button type="button" onClick={handleClear} style={getResponsiveStyle(baseStyles.clearButton,
                { padding: '0.875rem 1.5rem', fontSize: '1rem' },
                { padding: '1rem 2rem', fontSize: '1.05rem' },
                { padding: '1rem 2rem', fontSize: '1.05rem' }
              )}>
                {t('clearButton')}
              </button>
            </div>
          </form>

          {results && (
            <div style={getResponsiveStyle(baseStyles.results,
              { padding: '1.5rem' },
              { padding: '2rem' },
              { padding: '2rem' }
            )}>
              <h3 style={getResponsiveStyle(baseStyles.resultsTitle,
                { fontSize: '1.25rem', marginBottom: '1.5rem' },
                { fontSize: '1.5rem', marginBottom: '1.5rem' },
                { fontSize: '1.5rem', marginBottom: '1.5rem' }
              )}>{t('resultsTitle')}</h3>

              <div style={getResponsiveStyle(baseStyles.resultsGrid,
                { marginBottom: '1.5rem' },
                { marginBottom: '2rem' },
                { marginBottom: '2rem' }
              )}>
                <div style={getResponsiveStyle({ ...baseStyles.resultCard, ...baseStyles.resultCardPrimary },
                  { padding: '1.25rem' },
                  { padding: '1.25rem' },
                  { padding: '1.25rem' }
                )}>
                  <span style={getResponsiveStyle(baseStyles.resultLabel,
                    { marginBottom: '0.5rem' },
                    { marginBottom: '0.5rem' },
                    { marginBottom: '0.5rem' }
                  )}>{t('mostellerLabel')}</span>
                  <span style={getResponsiveStyle({ ...baseStyles.resultValue, ...baseStyles.resultValuePrimary },
                    { fontSize: '1.75rem' },
                    { fontSize: '2rem' },
                    { fontSize: '2rem' }
                  )}>{results.mosteller.toFixed(2)} m²</span>
                </div>
              </div>

              <div style={getResponsiveStyle(baseStyles.formulasSection,
                { marginTop: '1.5rem' },
                { marginTop: '2rem' },
                { marginTop: '2rem' }
              )}>
                <h4 style={getResponsiveStyle(baseStyles.h2,
                  { fontSize: '1.1rem', marginBottom: '1rem' },
                  { fontSize: '1.1rem', marginBottom: '1rem' },
                  { fontSize: '1.1rem', marginBottom: '1rem' }
                )}>{t('otherFormulasTitle')}</h4>
                <div style={getResponsiveStyle(baseStyles.formulaGrid,
                  { gridTemplateColumns: '1fr' },
                  { gridTemplateColumns: 'repeat(2, 1fr)' },
                  { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }
                )}>
                  <div style={baseStyles.formulaItem}>
                    <div style={baseStyles.formulaName}>{t('duboisFormula')}</div>
                    <div style={baseStyles.formulaValue}>{results.dubois.toFixed(2)} m²</div>
                  </div>
                  <div style={baseStyles.formulaItem}>
                    <div style={baseStyles.formulaName}>{t('haycockFormula')}</div>
                    <div style={baseStyles.formulaValue}>{results.haycock.toFixed(2)} m²</div>
                  </div>
                  <div style={baseStyles.formulaItem}>
                    <div style={baseStyles.formulaName}>{t('gehanFormula')}</div>
                    <div style={baseStyles.formulaValue}>{results.gehan.toFixed(2)} m²</div>
                  </div>
                  <div style={baseStyles.formulaItem}>
                    <div style={baseStyles.formulaName}>{t('boydFormula')}</div>
                    <div style={baseStyles.formulaValue}>{results.boyd.toFixed(2)} m²</div>
                  </div>
                  <div style={baseStyles.formulaItem}>
                    <div style={baseStyles.formulaName}>{t('fujimotoFormula')}</div>
                    <div style={baseStyles.formulaValue}>{results.fujimoto.toFixed(2)} m²</div>
                  </div>
                  <div style={baseStyles.formulaItem}>
                    <div style={baseStyles.formulaName}>{t('takahiraFormula')}</div>
                    <div style={baseStyles.formulaValue}>{results.takahira.toFixed(2)} m²</div>
                  </div>
                  <div style={baseStyles.formulaItem}>
                    <div style={baseStyles.formulaName}>{t('schlichFormula')}</div>
                    <div style={baseStyles.formulaValue}>{results.schlich.toFixed(2)} m²</div>
                  </div>
                </div>
              </div>

              <div style={getResponsiveStyle(baseStyles.infoBox,
                { marginTop: '1.5rem', padding: '1.25rem' },
                { marginTop: '1.5rem', padding: '1.25rem' },
                { marginTop: '1.5rem', padding: '1.25rem' }
              )}>
                <p style={baseStyles.infoBoxText}>{t('clinicalNote')}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={getResponsiveStyle(baseStyles.glassCard,
        { padding: '1.5rem', marginBottom: '1.5rem' },
        { padding: '2rem', marginBottom: '2rem' },
        { padding: '2.5rem', marginBottom: '2.5rem' }
      )}>
        <h2 style={getResponsiveStyle(baseStyles.h2,
          { fontSize: '1.5rem', marginBottom: '1rem' },
          { fontSize: '1.75rem', marginBottom: '1.25rem' },
          { fontSize: '2rem', marginBottom: '1.5rem' }
        )}>{t('introTitle')}</h2>
        <p style={getResponsiveStyle(baseStyles.paragraph,
          { fontSize: '1rem', marginBottom: '1rem' },
          { fontSize: '1.125rem', marginBottom: '1rem' },
          { fontSize: '1.25rem', marginBottom: '1rem' }
        )}>{t('introParagraph1')}</p>
        <p style={getResponsiveStyle(baseStyles.paragraph,
          { fontSize: '1rem', marginBottom: '1rem' },
          { fontSize: '1.125rem', marginBottom: '1rem' },
          { fontSize: '1.25rem', marginBottom: '1rem' }
        )}>{t('introParagraph2')}</p>
        <p style={getResponsiveStyle(baseStyles.paragraph,
          { fontSize: '1rem', marginBottom: '0' },
          { fontSize: '1.125rem', marginBottom: '0' },
          { fontSize: '1.25rem', marginBottom: '0' }
        )}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={getResponsiveStyle(baseStyles.glassCard,
        { padding: '1.5rem', marginBottom: '1.5rem' },
        { padding: '2rem', marginBottom: '2rem' },
        { padding: '2.5rem', marginBottom: '2.5rem' }
      )}>
        <h2 style={getResponsiveStyle(baseStyles.h2,
          { fontSize: '1.5rem', marginBottom: '1rem' },
          { fontSize: '1.75rem', marginBottom: '1.25rem' },
          { fontSize: '2rem', marginBottom: '1.5rem' }
        )}>{t('howToUseTitle')}</h2>
        <ol style={getResponsiveStyle(baseStyles.list,
          { fontSize: '1rem', paddingLeft: '1.5rem' },
          { fontSize: '1.125rem', paddingLeft: '1.5rem' },
          { fontSize: '1.25rem', paddingLeft: '1.5rem' }
        )}>
          <li style={baseStyles.listItem}>{t('howToStep1')}</li>
          <li style={baseStyles.listItem}>{t('howToStep2')}</li>
          <li style={baseStyles.listItem}>{t('howToStep3')}</li>
          <li style={baseStyles.listItem}>{t('howToStep4')}</li>
          <li style={baseStyles.listItem}>{t('howToStep5')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={getResponsiveStyle(baseStyles.glassCard,
        { padding: '1.5rem', marginBottom: '1.5rem' },
        { padding: '2rem', marginBottom: '2rem' },
        { padding: '2.5rem', marginBottom: '2.5rem' }
      )}>
        <h2 style={getResponsiveStyle(baseStyles.h2,
          { fontSize: '1.5rem', marginBottom: '1rem' },
          { fontSize: '1.75rem', marginBottom: '1.25rem' },
          { fontSize: '2rem', marginBottom: '1.5rem' }
        )}>{t('insightsTitle')}</h2>

        <div style={getResponsiveStyle(baseStyles.insightCard,
          { padding: '1rem', marginBottom: '1rem' },
          { padding: '1.25rem', marginBottom: '1.25rem' },
          { padding: '1.5rem', marginBottom: '1.5rem' }
        )}>
          <p style={getResponsiveStyle(baseStyles.paragraph,
            { fontSize: '1rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' },
            { fontSize: '1.25rem', marginBottom: '0' }
          )}><span style={baseStyles.strong}>{t('insight1Title')}</span> {t('insight1Text')}</p>
        </div>

        <div style={getResponsiveStyle(baseStyles.insightCard,
          { padding: '1rem', marginBottom: '1rem' },
          { padding: '1.25rem', marginBottom: '1.25rem' },
          { padding: '1.5rem', marginBottom: '1.5rem' }
        )}>
          <p style={getResponsiveStyle(baseStyles.paragraph,
            { fontSize: '1rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' },
            { fontSize: '1.25rem', marginBottom: '0' }
          )}><span style={baseStyles.strong}>{t('insight2Title')}</span> {t('insight2Text')}</p>
        </div>

        <div style={getResponsiveStyle(baseStyles.insightCard,
          { padding: '1rem', marginBottom: '1rem' },
          { padding: '1.25rem', marginBottom: '1.25rem' },
          { padding: '1.5rem', marginBottom: '1.5rem' }
        )}>
          <p style={getResponsiveStyle(baseStyles.paragraph,
            { fontSize: '1rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' },
            { fontSize: '1.25rem', marginBottom: '0' }
          )}><span style={baseStyles.strong}>{t('insight3Title')}</span> {t('insight3Text')}</p>
        </div>

        <div style={getResponsiveStyle(baseStyles.insightCard,
          { padding: '1rem', marginBottom: '1rem' },
          { padding: '1.25rem', marginBottom: '1.25rem' },
          { padding: '1.5rem', marginBottom: '1.5rem' }
        )}>
          <p style={getResponsiveStyle(baseStyles.paragraph,
            { fontSize: '1rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' },
            { fontSize: '1.25rem', marginBottom: '0' }
          )}><span style={baseStyles.strong}>{t('insight4Title')}</span> {t('insight4Text')}</p>
        </div>

        <div style={getResponsiveStyle(baseStyles.insightCard,
          { padding: '1rem', marginBottom: '0' },
          { padding: '1.25rem', marginBottom: '0' },
          { padding: '1.5rem', marginBottom: '0' }
        )}>
          <p style={getResponsiveStyle(baseStyles.paragraph,
            { fontSize: '1rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' },
            { fontSize: '1.25rem', marginBottom: '0' }
          )}><span style={baseStyles.strong}>{t('insight5Title')}</span> {t('insight5Text')}</p>
        </div>
      </section>

      {/* Details */}
      <section style={getResponsiveStyle(baseStyles.glassCard,
        { padding: '1.5rem', marginBottom: '1.5rem' },
        { padding: '2rem', marginBottom: '2rem' },
        { padding: '2.5rem', marginBottom: '2.5rem' }
      )}>
        <h2 style={getResponsiveStyle(baseStyles.h2,
          { fontSize: '1.5rem', marginBottom: '1rem' },
          { fontSize: '1.75rem', marginBottom: '1.25rem' },
          { fontSize: '2rem', marginBottom: '1.5rem' }
        )}>{t('detailsTitle')}</h2>

        <h3 style={getResponsiveStyle(baseStyles.h2,
          { fontSize: '1.25rem', marginBottom: '0.75rem', marginTop: '1.5rem' },
          { fontSize: '1.5rem', marginBottom: '1rem', marginTop: '2rem' },
          { fontSize: '1.75rem', marginBottom: '1rem', marginTop: '2rem' }
        )}>{t('detailsFormulasTitle')}</h3>
        <p style={getResponsiveStyle(baseStyles.paragraph,
          { fontSize: '1rem', marginBottom: '1rem' },
          { fontSize: '1.125rem', marginBottom: '1rem' },
          { fontSize: '1.25rem', marginBottom: '1rem' }
        )}>{t('detailsFormulasParagraph')}</p>

        <h3 style={getResponsiveStyle(baseStyles.h2,
          { fontSize: '1.25rem', marginBottom: '0.75rem', marginTop: '1.5rem' },
          { fontSize: '1.5rem', marginBottom: '1rem', marginTop: '2rem' },
          { fontSize: '1.75rem', marginBottom: '1rem', marginTop: '2rem' }
        )}>{t('detailsClinicalTitle')}</h3>
        <ul style={getResponsiveStyle(baseStyles.list,
          { fontSize: '1rem', paddingLeft: '1.5rem' },
          { fontSize: '1.125rem', paddingLeft: '1.5rem' },
          { fontSize: '1.25rem', paddingLeft: '1.5rem' }
        )}>
          <li style={baseStyles.listItem}>{t('detailsClinical1')}</li>
          <li style={baseStyles.listItem}>{t('detailsClinical2')}</li>
          <li style={baseStyles.listItem}>{t('detailsClinical3')}</li>
          <li style={baseStyles.listItem}>{t('detailsClinical4')}</li>
          <li style={baseStyles.listItem}>{t('detailsClinical5')}</li>
        </ul>

        <h3 style={getResponsiveStyle(baseStyles.h2,
          { fontSize: '1.25rem', marginBottom: '0.75rem', marginTop: '1.5rem' },
          { fontSize: '1.5rem', marginBottom: '1rem', marginTop: '2rem' },
          { fontSize: '1.75rem', marginBottom: '1rem', marginTop: '2rem' }
        )}>{t('detailsNormalTitle')}</h3>
        <p style={getResponsiveStyle(baseStyles.paragraph,
          { fontSize: '1rem', marginBottom: '0' },
          { fontSize: '1.125rem', marginBottom: '0' },
          { fontSize: '1.25rem', marginBottom: '0' }
        )}>{t('detailsNormalParagraph')}</p>
      </section>

      {/* FAQ */}
      <section style={getResponsiveStyle(baseStyles.glassCard,
        { padding: '1.5rem', marginBottom: '1.5rem' },
        { padding: '2rem', marginBottom: '2rem' },
        { padding: '2.5rem', marginBottom: '2.5rem' }
      )}>
        <h2 style={getResponsiveStyle(baseStyles.h2,
          { fontSize: '1.5rem', marginBottom: '1rem' },
          { fontSize: '1.75rem', marginBottom: '1.25rem' },
          { fontSize: '2rem', marginBottom: '1.5rem' }
        )}>{t('faqTitle')}</h2>

        <div style={getResponsiveStyle(baseStyles.faqItem,
          { padding: '1rem', marginBottom: '1rem' },
          { padding: '1.25rem', marginBottom: '1.25rem' },
          { padding: '1.5rem', marginBottom: '1.5rem' }
        )}>
          <h3 style={getResponsiveStyle(baseStyles.faqQuestion,
            { fontSize: '1.1rem', marginBottom: '0.5rem' },
            { fontSize: '1.25rem', marginBottom: '0.75rem' },
            { fontSize: '1.25rem', marginBottom: '0.75rem' }
          )}>{t('faq1Question')}</h3>
          <p style={getResponsiveStyle(baseStyles.faqAnswer,
            { fontSize: '1rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' }
          )}>{t('faq1Answer')}</p>
        </div>

        <div style={getResponsiveStyle(baseStyles.faqItem,
          { padding: '1rem', marginBottom: '1rem' },
          { padding: '1.25rem', marginBottom: '1.25rem' },
          { padding: '1.5rem', marginBottom: '1.5rem' }
        )}>
          <h3 style={getResponsiveStyle(baseStyles.faqQuestion,
            { fontSize: '1.1rem', marginBottom: '0.5rem' },
            { fontSize: '1.25rem', marginBottom: '0.75rem' },
            { fontSize: '1.25rem', marginBottom: '0.75rem' }
          )}>{t('faq2Question')}</h3>
          <p style={getResponsiveStyle(baseStyles.faqAnswer,
            { fontSize: '1rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' }
          )}>{t('faq2Answer')}</p>
        </div>

        <div style={getResponsiveStyle(baseStyles.faqItem,
          { padding: '1rem', marginBottom: '1rem' },
          { padding: '1.25rem', marginBottom: '1.25rem' },
          { padding: '1.5rem', marginBottom: '1.5rem' }
        )}>
          <h3 style={getResponsiveStyle(baseStyles.faqQuestion,
            { fontSize: '1.1rem', marginBottom: '0.5rem' },
            { fontSize: '1.25rem', marginBottom: '0.75rem' },
            { fontSize: '1.25rem', marginBottom: '0.75rem' }
          )}>{t('faq3Question')}</h3>
          <p style={getResponsiveStyle(baseStyles.faqAnswer,
            { fontSize: '1rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' }
          )}>{t('faq3Answer')}</p>
        </div>

        <div style={getResponsiveStyle(baseStyles.faqItem,
          { padding: '1rem', marginBottom: '1rem' },
          { padding: '1.25rem', marginBottom: '1.25rem' },
          { padding: '1.5rem', marginBottom: '1.5rem' }
        )}>
          <h3 style={getResponsiveStyle(baseStyles.faqQuestion,
            { fontSize: '1.1rem', marginBottom: '0.5rem' },
            { fontSize: '1.25rem', marginBottom: '0.75rem' },
            { fontSize: '1.25rem', marginBottom: '0.75rem' }
          )}>{t('faq4Question')}</h3>
          <p style={getResponsiveStyle(baseStyles.faqAnswer,
            { fontSize: '1rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' }
          )}>{t('faq4Answer')}</p>
        </div>

        <div style={getResponsiveStyle(baseStyles.faqItem,
          { padding: '1rem', marginBottom: '1rem' },
          { padding: '1.25rem', marginBottom: '1.25rem' },
          { padding: '1.5rem', marginBottom: '1.5rem' }
        )}>
          <h3 style={getResponsiveStyle(baseStyles.faqQuestion,
            { fontSize: '1.1rem', marginBottom: '0.5rem' },
            { fontSize: '1.25rem', marginBottom: '0.75rem' },
            { fontSize: '1.25rem', marginBottom: '0.75rem' }
          )}>{t('faq5Question')}</h3>
          <p style={getResponsiveStyle(baseStyles.faqAnswer,
            { fontSize: '1rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' }
          )}>{t('faq5Answer')}</p>
        </div>

        <div style={getResponsiveStyle(baseStyles.faqItem,
          { padding: '1rem', marginBottom: '0' },
          { padding: '1.25rem', marginBottom: '0' },
          { padding: '1.5rem', marginBottom: '0' }
        )}>
          <h3 style={getResponsiveStyle(baseStyles.faqQuestion,
            { fontSize: '1.1rem', marginBottom: '0.5rem' },
            { fontSize: '1.25rem', marginBottom: '0.75rem' },
            { fontSize: '1.25rem', marginBottom: '0.75rem' }
          )}>{t('faq6Question')}</h3>
          <p style={getResponsiveStyle(baseStyles.faqAnswer,
            { fontSize: '1rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' },
            { fontSize: '1.125rem', marginBottom: '0' }
          )}>{t('faq6Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={getResponsiveStyle(baseStyles.glassCard,
        { padding: '1.5rem', marginBottom: '1.5rem' },
        { padding: '2rem', marginBottom: '2rem' },
        { padding: '2.5rem', marginBottom: '2.5rem' }
      )}>
        <h2 style={getResponsiveStyle(baseStyles.h2,
          { fontSize: '1.5rem', marginBottom: '1rem' },
          { fontSize: '1.75rem', marginBottom: '1.25rem' },
          { fontSize: '2rem', marginBottom: '1.5rem' }
        )}>{t('referencesTitle')}</h2>
        <ul style={baseStyles.referenceList}>
          <li style={getResponsiveStyle(baseStyles.referenceItem,
            { marginBottom: '0.75rem' },
            { marginBottom: '1rem' },
            { marginBottom: '1rem' }
          )}>
            <a href="https://cdnproxy.thepoint.lww.com/download/wolterskluwer_vitalstream_com/journal_library/nsg_03604039_2019_49_9_51.pdf"
               target="_blank"
               rel="noopener noreferrer"
               style={getResponsiveStyle(baseStyles.referenceLink,
                 { fontSize: '0.95rem' },
                 { fontSize: '1rem' },
                 { fontSize: '1rem' }
               )}>
              {t('reference1')}
            </a>
          </li>
          <li style={getResponsiveStyle(baseStyles.referenceItem,
            { marginBottom: '0.75rem' },
            { marginBottom: '1rem' },
            { marginBottom: '1rem' }
          )}>
            <a href="https://www.rxlist.com/body_surface_area/definition.htm"
               target="_blank"
               rel="noopener noreferrer"
               style={getResponsiveStyle(baseStyles.referenceLink,
                 { fontSize: '0.95rem' },
                 { fontSize: '1rem' },
                 { fontSize: '1rem' }
               )}>
              {t('reference2')}
            </a>
          </li>
          <li style={getResponsiveStyle(baseStyles.referenceItem,
            { marginBottom: '0.75rem' },
            { marginBottom: '1rem' },
            { marginBottom: '1rem' }
          )}>
            <a href="https://en.wikipedia.org/wiki/Body_surface_area"
               target="_blank"
               rel="noopener noreferrer"
               style={getResponsiveStyle(baseStyles.referenceLink,
                 { fontSize: '0.95rem' },
                 { fontSize: '1rem' },
                 { fontSize: '1rem' }
               )}>
              {t('reference3')}
            </a>
          </li>
          <li style={getResponsiveStyle(baseStyles.referenceItem,
            { marginBottom: '0.75rem' },
            { marginBottom: '1rem' },
            { marginBottom: '1rem' }
          )}>
            <a href="https://www.ncbi.nlm.nih.gov/books/NBK559005/"
               target="_blank"
               rel="noopener noreferrer"
               style={getResponsiveStyle(baseStyles.referenceLink,
                 { fontSize: '0.95rem' },
                 { fontSize: '1rem' },
                 { fontSize: '1rem' }
               )}>
              {t('reference4')}
            </a>
          </li>
          <li style={getResponsiveStyle(baseStyles.referenceItem,
            { marginBottom: '0' },
            { marginBottom: '0' },
            { marginBottom: '0' }
          )}>
            <a href="https://www.calculator.net/body-surface-area-calculator.html"
               target="_blank"
               rel="noopener noreferrer"
               style={getResponsiveStyle(baseStyles.referenceLink,
                 { fontSize: '0.95rem' },
                 { fontSize: '1rem' },
                 { fontSize: '1rem' }
               )}>
              {t('reference5')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}